// Crash/error reporting for the web app.
//
// AppOrigin Chat is a privacy-focused messenger, so the bar for what may leave the
// browser is much higher than for a normal web app: an error report must be able to tell
// us *that* something broke and *where in the code*, and nothing else. Concretely, none
// of the following may ever reach the collector:
//
//   * message text, drafts, attachment contents or file names,
//   * contact/topic identifiers (uid `usr…`, `grp…`, `chn…`), display names, phone
//     numbers, email addresses,
//   * credentials: passwords, login/registration secrets, auth tokens, FCM device
//     tokens, media encryption keys,
//   * the `apikey` query parameter, which the SDK appends to *every* websocket and file
//     URL, and which is therefore present in almost every URL this app touches.
//
// Everything below is built around that. The strategy is deny-by-default: we hand-pick
// the SDK integrations instead of taking the defaults, we turn off every integration
// that harvests page/user data, and then we still run a recursive scrubber over the
// finished event as a backstop in case the SDK (or a future upgrade) adds a field we did
// not anticipate.

import * as Sentry from '@sentry/browser';

import { PACKAGE_VERSION } from '../version.js';

// Query/POST parameter names whose *value* is always secret. Matched case-insensitively
// anywhere in a string, not just in a parsed URL, because error messages routinely
// interpolate a whole URL ("failed to fetch http://host/v0/file/s/x?apikey=…").
// `content`/`text`/`draft`/`msg` are in the list because an error message that
// interpolates them is interpolating what the user typed.
// The value alternation matches a quoted value FIRST, including spaces: `draft='meet me
// at the safehouse'` must be redacted whole, not truncated at the first space (which is
// what an unquoted-only pattern does, leaving the sentence behind).
const SECRET_PARAM_RE =
  /\b(apikey|api_key|token|access_token|id_token|refresh_token|auth|authorization|secret|password|passwd|pwd|sid|session|cred|credential|key|signature|sig|content|text|draft|msg|body)\b\s*[=:]\s*(?:"[^"]*"|'[^']*'|[^&\s"'#,;)]+)/gi;

// `Authorization: Bearer …` / `Token …` style headers pasted into a message.
const BEARER_RE = /\b(bearer|basic|token)\s+[A-Za-z0-9._~+/=-]{8,}/gi;

// Tinode topic/user identifiers. These are pseudonymous, but they *are* the identity of
// a conversation partner: two events carrying the same `usr…` prove the same person was
// involved. They are contact info for our purposes, so they get collapsed to their kind.
const TOPIC_ID_RE = /\b(usr|grp|chn|nch)[A-Za-z0-9_-]{6,}/g;

// Redact secrets from a free-form string. Applied to every string in the event.
export function redactString(str) {
  if (typeof str != 'string' || !str) {
    return str;
  }
  // BEARER_RE must run FIRST. `Authorization: Bearer eyJ…` matches SECRET_PARAM_RE on the
  // `authorization:` prefix, which consumes only the word "Bearer" as the value and
  // leaves the actual token in the string. Stripping the scheme+token pair first avoids
  // that. (Verified: this exact case leaked before the reorder.)
  return str
    .replace(BEARER_RE, (_m, scheme) => `${scheme} <redacted>`)
    .replace(SECRET_PARAM_RE, (_m, name) => `${name}=<redacted>`)
    .replace(TOPIC_ID_RE, (_m, kind) => `${kind}<redacted>`);
}

// Reduce a URL to path-only. The query string is dropped wholesale rather than
// filtered: the app talks to the server with `?apikey=…` on the websocket and with
// per-file auth on uploads, and a future parameter we have not thought of would
// otherwise leak by default. The hash is collapsed to the *kind* of route, because the
// app's hash is `#<topic-id>?params` -- keeping "the user was looking at a group topic"
// is useful, keeping *which* group is not.
export function sanitizeUrl(url) {
  if (typeof url != 'string' || !url) {
    return url;
  }
  let out = url;
  let hash = '';
  const hashAt = out.indexOf('#');
  if (hashAt >= 0) {
    hash = out.slice(hashAt + 1);
    out = out.slice(0, hashAt);
  }
  const queryAt = out.indexOf('?');
  if (queryAt >= 0) {
    // Keep a marker so it is obvious in the UI that a query existed and was removed,
    // rather than looking like the request had none.
    out = out.slice(0, queryAt) + '?<redacted>';
  }
  if (hash) {
    const route = hash.split('?')[0];
    const kind = /^(usr|grp|chn|nch)/.exec(route);
    out += '#' + (kind ? kind[1] + '<redacted>' : route === '' ? '' : redactString(route));
  }
  return out;
}

// Keys that must never be transmitted regardless of where they show up in the event
// tree. `data`/`body` cover request payloads; the storage keys cover the "helpful"
// localStorage/sessionStorage dumps some SDK versions and custom contexts attach --
// this app stores the auth token, the FCM device token and the outbox (i.e. unsent
// message text) in localStorage, so a storage dump would be a total leak.
const FORBIDDEN_KEYS = new Set([
  'data', 'body', 'payload', 'content', 'message_text', 'draft',
  'localstorage', 'local_storage', 'sessionstorage', 'session_storage', 'storage',
  'cookies', 'cookie', 'headers', 'query_string', 'querystring',
  'password', 'secret', 'token', 'apikey', 'api_key', 'authorization'
]);

// Keys whose values are URLs and therefore get sanitizeUrl() rather than a blanket drop.
const URL_KEYS = new Set(['url', 'href', 'origin_url', 'referrer', 'from', 'to']);

const MAX_DEPTH = 6;

// Recursive backstop scrubber. Runs over the *assembled* event, so it also covers
// anything an integration added that we did not explicitly configure.
function scrub(value, depth, keyHint) {
  if (depth > MAX_DEPTH) {
    return '<truncated>';
  }
  if (typeof value == 'string') {
    return URL_KEYS.has(keyHint) ? sanitizeUrl(value) : redactString(value);
  }
  if (!value || typeof value != 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(v => scrub(v, depth + 1, keyHint));
  }
  const out = {};
  for (const k of Object.keys(value)) {
    const lower = k.toLowerCase();
    if (FORBIDDEN_KEYS.has(lower)) {
      out[k] = '<redacted>';
      continue;
    }
    out[k] = scrub(value[k], depth + 1, lower);
  }
  return out;
}

// Some event fields are structural (stack frames, event id, timestamps) and would be
// damaged by the recursive scrubber, or are simply large. Scrub the parts that can hold
// user data and leave the machinery alone.
function scrubEvent(event) {
  if (!event) {
    return event;
  }

  // No user identity, ever. sendDefaultPii:false already suppresses the IP address, but
  // the SDK will still forward whatever `setUser()` put there, and our uid is contact
  // info. Nothing in this app calls setUser(); this makes that a guarantee rather than
  // a convention.
  delete event.user;

  if (event.request) {
    // Keep only the (sanitized) URL. Headers can carry Cookie/Authorization, `data` is
    // the POST body, `query_string` is where `apikey` lives.
    const url = sanitizeUrl(event.request.url);
    event.request = url ? {url: url} : undefined;
  }

  if (event.message) {
    event.message = redactString(event.message);
  }
  if (event.transaction) {
    event.transaction = redactString(event.transaction);
  }

  if (event.exception && Array.isArray(event.exception.values)) {
    for (const ex of event.exception.values) {
      // The *type* is a class name (safe). The *value* is the thrown message, which is
      // the single most likely place for a URL-with-apikey or an interpolated topic id.
      ex.value = redactString(ex.value);
      // Frame filenames are our bundle URLs; they can carry a cache-busting query.
      if (ex.stacktrace && Array.isArray(ex.stacktrace.frames)) {
        for (const f of ex.stacktrace.frames) {
          f.filename = sanitizeUrl(f.filename);
          f.abs_path = sanitizeUrl(f.abs_path);
          // Local variable capture is not enabled in the browser SDK, but if it ever
          // becomes a default it would dump message text straight out. Drop it.
          delete f.vars;
        }
      }
    }
  }

  if (Array.isArray(event.breadcrumbs)) {
    // Go through scrubBreadcrumb, not plain scrub(): the latter would blanket-redact the
    // crumb's `data` a second time and undo the URL-aware handling done there.
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter(Boolean);
  }
  // Nothing in this app sets `extra` -- reportError() uses tags. Anything present was put
  // there by an integration: browserApiErrors attaches `extra.arguments`, i.e. the
  // arguments the failing setTimeout/addEventListener handler was called with, which in
  // this app can be a whole message packet. Drop the field rather than scrub it.
  delete event.extra;
  if (event.contexts) {
    // `contexts.state` is where redux/state-snapshot plugins park an entire app store.
    delete event.contexts.state;
    event.contexts = scrub(event.contexts, 0, null);
  }
  if (event.tags) {
    event.tags = scrub(event.tags, 0, null);
  }

  return event;
}

// Breadcrumb-level filter. This is the second half of the story: even with the console/
// DOM/network breadcrumb collectors disabled (see below), a breadcrumb added manually or
// by a future integration still passes through here.
function scrubBreadcrumb(crumb) {
  if (!crumb) {
    return null;
  }
  // Belt and braces: if a console breadcrumb ever appears -- because someone re-enables
  // the collector, or an integration synthesizes one -- drop it outright. In this app
  // console output includes the Tinode SDK packet log, i.e. full message payloads and
  // login credentials.
  if (crumb.category === 'console') {
    return null;
  }
  // Same for DOM interaction breadcrumbs: their `message` is a CSS-ish selector that can
  // include element text (contact names, message snippets).
  if (crumb.category === 'ui.click' || crumb.category === 'ui.input') {
    return null;
  }
  // `data` is in FORBIDDEN_KEYS (it is a request body everywhere else in an event), but
  // on a breadcrumb it is the structured payload -- for the navigation crumbs we keep,
  // that is {from, to}. Blanket-redacting it throws away the only thing the crumb is for.
  // So scrub the breadcrumb without its data, then scrub the data one level in, where
  // `from`/`to` are recognised as URLs and get sanitizeUrl(); any nested body/content/
  // token key inside is still caught by FORBIDDEN_KEYS.
  const {data, ...rest} = crumb;
  const out = scrub(rest, 0, null);
  if (data && typeof data == 'object') {
    out.data = scrub(data, 1, null);
  }
  return out;
}

let initialized = false;

// True once init() actually armed the SDK. Used by the callers below so they do not have
// to care whether reporting is configured in this deployment.
export function isErrorReportingEnabled() {
  return initialized;
}

export function initErrorReporting() {
  if (initialized) {
    return;
  }
  const cfg = (typeof SENTRY_INIT != 'undefined' && SENTRY_INIT) || null;
  if (!cfg || !cfg.dsn || cfg.enabled === false) {
    // Reporting is simply not configured for this deployment. Not an error.
    return;
  }

  Sentry.init({
    dsn: cfg.dsn,
    environment: cfg.environment || 'production',
    // Ties an event to the exact bundle. scripts/gen-version.js regenerates
    // src/version.js from package.json on every `npm run build`, so this is always the
    // built version without a second source of truth.
    release: 'apporigin-chat-web@' + (PACKAGE_VERSION || '0.0.0'),
    sampleRate: typeof cfg.sampleRate == 'number' ? cfg.sampleRate : 1.0,

    // ---- privacy-critical switches -------------------------------------------------
    // Never attach IP address, cookies, or request headers.
    sendDefaultPii: false,
    // No performance tracing. Transaction names in this app are URLs, and this app's
    // URLs carry `apikey` and topic ids. Tracing would also mean a span per fetch/XHR,
    // i.e. a continuous stream of attachment URLs leaving the browser even when nothing
    // is broken.
    tracesSampleRate: 0,
    // No Session Replay under any circumstance: it records the DOM, which in a messenger
    // *is* the message history. Not merely set to 0 -- the integration is not bundled
    // in at all (see `integrations` below).
    // ---------------------------------------------------------------------------------

    // Serialize at most this deep when turning objects into event data; keeps a stray
    // rich object (e.g. a topic instance) from being expanded wholesale.
    normalizeDepth: 3,
    maxBreadcrumbs: 20,
    // Synthesize a stack trace for non-Error throws (`throw 'oops'`), which the Tinode
    // SDK does in a couple of places.
    attachStacktrace: true,

    // REQUIRED for the list below to mean what it says. Passing `integrations` alone
    // *merges* with the SDK defaults rather than replacing them -- the first version of
    // this file did that and shipped BrowserSession (release-health pings) and
    // CultureContext (locale + timezone, i.e. fingerprinting surface) that were never
    // asked for. With defaults off, the array below is the complete, audited set, and an
    // SDK upgrade cannot quietly switch a new data collector on.
    defaultIntegrations: false,

    // Hand-picked integrations.
    integrations: [
      // Required for window.onerror + unhandledrejection. Both on: an unhandled promise
      // rejection is the dominant failure shape in this codebase (every SDK call is a
      // promise, and several `.catch()` handlers only console.error).
      Sentry.globalHandlersIntegration({onerror: true, onunhandledrejection: true}),
      // Wraps setTimeout/requestAnimationFrame/addEventListener so errors thrown inside
      // them keep a usable stack instead of arriving as "Script error". Collects no data.
      Sentry.browserApiErrorsIntegration(),
      // Follows `error.cause` chains. Values are redacted by beforeSend like any other.
      Sentry.linkedErrorsIntegration(),
      // Suppresses duplicate reports of the same error. Pure noise control.
      Sentry.dedupeIntegration(),
      // Drops known-noise errors (ResizeObserver loop, browser extension errors).
      Sentry.eventFiltersIntegration(),
      // Makes minified function names print sensibly. No data collection.
      Sentry.functionToStringIntegration(),
      // Sets request.url + User-Agent. We keep it because the URL (after sanitizeUrl)
      // is genuinely useful, and beforeSend strips the query string and the topic id.
      Sentry.httpContextIntegration(),
      // Breadcrumbs, with every content-bearing collector off:
      Sentry.breadcrumbsIntegration({
        // OFF. The Tinode SDK logs every websocket packet to the console when
        // LOGGING_ENABLED is true (it is), so console breadcrumbs would ship plaintext
        // message content, contact lists and the login handshake. This is the single
        // biggest leak vector in this app.
        console: false,
        // OFF. DOM breadcrumbs include the text content of the clicked element:
        // contact names and message bubbles.
        dom: false,
        // OFF. Request URLs are attachment/avatar URLs that identify a conversation and
        // carry auth parameters; the breadcrumb also records status codes per message
        // fetch, which is a traffic-analysis side channel.
        fetch: false,
        xhr: false,
        // ON. Route changes only, and the route is collapsed to `#grp<redacted>` in
        // sanitizeUrl(). Knowing "the crash happened right after switching topics" is
        // worth a lot and costs nothing identifying.
        history: true,
        // OFF. Sentry's own request lifecycle; noise.
        sentry: false
      })
      // Deliberately absent: replayIntegration, browserTracingIntegration,
      // captureConsoleIntegration, httpClientIntegration (sends request/response
      // headers and bodies), reportingObserverIntegration (reports CSP violations
      // including blocked URLs), extraErrorDataIntegration (serializes arbitrary
      // properties hanging off the error object), contextLinesIntegration,
      // browserSessionIntegration.
    ],

    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: (event) => {
      try {
        return scrubEvent(event);
      } catch (err) {
        // If the scrubber itself throws we must NOT fall back to sending the raw event.
        // Dropping a crash report is always preferable to leaking a message.
        return null;
      }
    }
  });

  initialized = true;
}

// Report a handled error that would otherwise be swallowed or only console.error'd.
//
// `context` must contain only literals the caller chose on purpose -- never an error
// payload, a topic name or anything derived from user input. It is scrubbed anyway, but
// the scrubber is a backstop, not a licence.
export function reportError(err, context) {
  if (!initialized) {
    return;
  }
  try {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      if (context) {
        for (const k of Object.keys(context)) {
          scope.setTag(k, String(context[k]));
        }
      }
      if (err instanceof Error) {
        Sentry.captureException(err);
      } else {
        // Non-Error throw: keep the shape but do not stringify an arbitrary object,
        // which could be a server packet containing message content.
        Sentry.captureMessage(typeof err == 'string' ? err : 'Non-Error thrown: ' + typeof err);
      }
    });
  } catch (e) {
    // Reporting must never break the app.
  }
}

// Deliberate test error, used to prove the pipeline end to end.
//
// Thrown asynchronously so it goes through the browser's own error path (window.onerror
// via globalHandlersIntegration) rather than through captureException -- that is what we
// actually want to verify, and it is what a real crash looks like.
export function throwTestError(label) {
  const tag = label ? String(label).slice(0, 40) : 'manual';
  setTimeout(_ => {
    throw new Error('AppOrigin Chat test error (' + tag + ')');
  }, 0);
  return 'test error scheduled';
}
